import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

import { sortCountriesByName } from 'src/app/helpers/utils/country.utils';

import { CountryInfo, PhoneCode, PhoneSettings, Region } from 'src/app/shared/models/country.model';

import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CountryService {
  private _selectedCountry$ = new BehaviorSubject<CountryInfo>(null);

  public phoneCodeCache: PhoneCode[] = null;
  public countryCodeCache: CountryInfo[] = null;
  public regionCache: Region[][] = [];

  public countries = new BehaviorSubject<CountryInfo[]>(null);
  public phones = new BehaviorSubject<PhoneCode[]>(null);

  public selectedCountry$ = this._selectedCountry$.asObservable();

  constructor(private http: HttpClient) {}

  public getFiscalStatusRaw(countryCode: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}business-reference/fiscalstatusraw/${countryCode}`);
  }

  public findCountryByISO3Code(countryCode: string): CountryInfo {
    return this.countries.value.find(country=> country.countryIsoCode3 === countryCode)
  }

  public setSelectedCountry(countryCode: string): void {
    const country = this.findCountryByISO3Code(countryCode);
    this._selectedCountry$.next(country);
  }

  public getSelectedCountry(): Observable<CountryInfo> {
    return this.selectedCountry$.pipe(distinctUntilChanged());
  }

  public loadCountryOptions(): Observable<[CountryInfo[], PhoneSettings]> {
    if (this.countryCodeCache && this.phoneCodeCache) {
      return of(null);
    }

    return forkJoin([
      this.http.get(`${environment.apiUrl}static-reference/countries`),
      this.http.get(`${environment.apiUrl}phones/search/phones-settings`)
    ]).pipe(
      tap(([countriesList, phoneSettings]: [CountryInfo[], PhoneSettings]) => {
        const sortedCountriesList = sortCountriesByName(countriesList);
        this.countryCodeCache = sortedCountriesList;
        this.countries.next(sortedCountriesList);
        this.setPhones(phoneSettings);
      })
    );
  }

  public setPhones(phoneSettings: PhoneSettings): void {
    const codes = phoneSettings.placeholders || [];
    let result = [];
    codes.forEach((code) => {
      const mappedCountry = this.countryCodeCache.find(
        (country) => country['countryIsoCode2'] === code.countryIsoCode2
      );
      if (mappedCountry) {
        result.push({
          ...mappedCountry,
          errorPlaceholder: code.formattedPhoneNumbers[0]
        });
      }
    });
    result = sortCountriesByName(result);
    this.phoneCodeCache = result;
    this.phones.next(result);
  }

  public regions(countryIsoCode3: string): Observable<Region[]> {
    if (this.regionCache[countryIsoCode3] && this.regionCache[countryIsoCode3].length) {
      return of(this.regionCache[countryIsoCode3]);
    }

    const request = this.http.get(`${environment.apiUrl}static-reference/countries/${countryIsoCode3}/states`);

    return request.pipe(
      map((regions: Region[]) => {
        this.regionCache[countryIsoCode3] = regions;
        return regions;
      })
    );
  }

  getTaxOffices(countryIsoCode3: string): Observable<{ countryISONumber: string; id: string; county: string }[]> {
    return this.http.get<{ countryISONumber: string; id: string; county: string }[]>(
      `${environment.apiUrl}business-reference/taxoffices/${countryIsoCode3}`
    );
  }

  getTaxObligation(countryIsoCode3: string): Observable<{ countryISONumber: string; id: string; county: string }[]> {
    return this.http.get<{ countryISONumber: string; id: string; county: string }[]>(
      `${environment.apiUrl}business-reference/taxobligations/${countryIsoCode3}`
    );
  }

  getMunicipality(countryIsoCode3: string): Observable<string[]> {
    return this.http.get<string[]>(`${environment.apiUrl}business-reference/municipalities/${countryIsoCode3}`);
  }
}
